import { FlexBox, LoadingButton } from '@airship-platform/jam-web/components';
import { EmailValidationSchema } from '@airship-platform/common/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAccountManagement } from './contexts/AccountManagementContext';

const formSchema = z.object({
  email: EmailValidationSchema.min(1, 'Email is required'),
});

type ForgotPasswordFormValues = z.infer<typeof formSchema>;

const ForgotPasswordPage: React.FC = () => {
  const [success, setSuccess] = useState(false);
  const { forgotPassword, isLoading, error } = useAccountManagement();

  const { control, handleSubmit } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    try {
      await forgotPassword(data.email);
      setSuccess(true);
    } catch {
      setSuccess(false);
    }
  };

  return (
    <FlexBox
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      p={3}
    >
      <FlexBox
        flexDirection={'column'}
        alignItems={'center'}
        textAlign={'center'}
        width={'25rem'}
        gap={3}
      >
        <Typography variant="h2" align="center">
          {success ? 'Check your email' : 'Reset Password'}
        </Typography>
        {success ? (
          <Typography variant="body1">
            We've sent you an email containing instructions for resetting your
            password.
          </Typography>
        ) : (
          <>
            <Typography variant="body1">
              Enter the email address associated with your account and we'll
              send you a link to reset your password.
            </Typography>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label="Email address"
                  {...field}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error ? fieldState.error.message : error || ''
                  }
                  fullWidth
                />
              )}
            />
            <LoadingButton
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
              fullWidth
            >
              Send reset link
            </LoadingButton>
          </>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export default ForgotPasswordPage;
